import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEO from "@/components/base/SEO";

export default function UploadPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ title: "", category: "", price: "", stock: "", description: "", trackingNumber: "", customerEmail: "" });
  const [submitted, setSubmitted] = useState(false);
  const [trackingSent, setTrackingSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: "#f5f5f5"}}>
      <SEO title="Upload Products — Factory Portal | StarBookPrime" description="Upload and publish your factory products to the StarBookPrime marketplace." canonical="/upload" noIndex={true} />
      <Navbar />
      <div className="pt-20 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-3">Verified Factory Portal</span>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Upload Your Products</h1>
            <p className="text-gray-500">List your products and reach millions of local buyers across the USA</p>
          </div>

          {/* Verification Badge Banner */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 mb-8">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
              <i className="ri-shield-check-fill text-xl" />
            </div>
            <div>
              <div className="font-semibold text-green-800 text-sm">Your factory is Verified</div>
              <div className="text-green-600 text-xs">Products will display the Verified Factory badge — boosting buyer trust and sales</div>
            </div>
            <div className="ml-auto">
              <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">✓ VERIFIED</span>
            </div>
          </div>

          {/* Steps */}
          <div className="flex items-center gap-2 mb-8">
            {["Product Info", "Pricing & Stock", "Review & Publish"].map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${step > i + 1 ? "bg-green-500 text-white" : step === i + 1 ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-400"}`}>
                  {step > i + 1 ? <i className="ri-check-line" /> : i + 1}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap ${step === i + 1 ? "text-orange-500" : "text-gray-400"}`}>{s}</span>
                {i < 2 && <div className="flex-1 h-px bg-gray-200 mx-2" />}
              </div>
            ))}
          </div>

          {!submitted ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              {step === 1 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Product Information</h2>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Title *</label>
                    <input name="title" value={form.title} onChange={handleChange} placeholder="e.g. Premium Wireless Earbuds Pro X200" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-orange-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
                    <select name="category" value={form.category} onChange={handleChange} className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-orange-400 cursor-pointer">
                      <option value="">Select category</option>
                      {["Electronics", "Fashion", "Home", "Beauty", "Sports", "Food", "Other"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Description *</label>
                    <textarea name="description" value={form.description} onChange={handleChange} rows={4} maxLength={500} placeholder="Describe your product features, materials, specifications..." className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-orange-400 resize-none transition-colors" />
                    <div className="text-xs text-gray-400 text-right mt-1">{form.description.length}/500</div>
                  </div>
                  {/* Image Upload Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Images</label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-orange-300 transition-colors cursor-pointer">
                      <i className="ri-image-add-line text-3xl text-gray-300 mb-2 block" />
                      <p className="text-sm text-gray-500">Drag & drop images or <span className="text-orange-500 font-semibold">browse</span></p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB each (max 8 images)</p>
                    </div>
                  </div>
                  <button onClick={() => setStep(2)} className="w-full py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                    Continue to Pricing →
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Pricing & Inventory</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Sale Price (USD) *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input name="price" value={form.price} onChange={handleChange} type="number" placeholder="0.00" className="w-full border border-gray-200 rounded-lg pl-7 pr-4 py-3 text-sm outline-none focus:border-orange-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Stock Quantity *</label>
                      <input name="stock" value={form.stock} onChange={handleChange} type="number" placeholder="e.g. 500" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-orange-400" />
                    </div>
                  </div>
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-orange-800 mb-2 flex items-center gap-2"><i className="ri-user-star-line" /> Influencer Commission Settings</h3>
                    <p className="text-xs text-orange-600 mb-3">Allow influencers (TikTok, YouTube, Twitter stars) to promote and earn commission on your product.</p>
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-700">Commission Rate:</label>
                      <select className="border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white cursor-pointer outline-none">
                        {["5%", "8%", "10%", "12%", "15%"].map((r) => <option key={r}>{r}</option>)}
                      </select>
                      <span className="text-xs text-gray-500">of sale price per order</span>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2"><i className="ri-global-line" /> Overseas Buyer Quota</h3>
                    <p className="text-xs text-blue-600 mb-3">Enable international buyers to purchase this product under quota allocation.</p>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" id="overseas" className="cursor-pointer" />
                      <label htmlFor="overseas" className="text-sm text-gray-700 cursor-pointer">Enable overseas buyer access</label>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(1)} className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">← Back</button>
                    <button onClick={() => setStep(3)} className="flex-1 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">Review Product →</button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Review & Publish</h2>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                    {[
                      { label: "Title", value: form.title || "Premium Wireless Earbuds Pro X200" },
                      { label: "Category", value: form.category || "Electronics" },
                      { label: "Price", value: form.price ? `$${form.price}` : "$49.99" },
                      { label: "Stock", value: form.stock || "500 units" },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between text-sm">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="font-semibold text-gray-900">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">← Back</button>
                    <button onClick={() => setSubmitted(true)} className="flex-1 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-rocket-line mr-2" />Publish Product
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-500 text-3xl mx-auto mb-4"><i className="ri-check-double-line" /></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Published!</h2>
              <p className="text-gray-500 mb-8">Your product is now live on StarBookPrime marketplace.</p>

              {/* Tracking Number Section */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-left mb-6">
                <h3 className="font-bold text-orange-800 mb-1 flex items-center gap-2"><i className="ri-truck-line" /> Send Tracking Number to Customer</h3>
                <p className="text-xs text-orange-600 mb-4">Once you ship an order, enter the tracking number below. The customer will receive an email and SMS notification automatically.</p>
                <div className="space-y-3">
                  <input name="customerEmail" value={form.customerEmail} onChange={handleChange} placeholder="Customer email or phone" className="w-full border border-orange-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 bg-white" />
                  <input name="trackingNumber" value={form.trackingNumber} onChange={handleChange} placeholder="Tracking number (e.g. 1Z999AA10123456784)" className="w-full border border-orange-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 bg-white font-mono" />
                  {!trackingSent ? (
                    <button onClick={() => setTrackingSent(true)} className="w-full py-2.5 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-send-plane-line mr-2" />Send Tracking Notification
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600 font-semibold text-sm justify-center py-2">
                      <i className="ri-checkbox-circle-line" /> Tracking notification sent to customer!
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => { setSubmitted(false); setStep(1); setTrackingSent(false); }} className="px-8 py-3 bg-gray-900 text-white font-bold rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Upload Another Product
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
